import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Boards from './pages/Boards';
import BoardDetail from './pages/BoardDetail';
import MyWork from './pages/MyWork';
import GlobalTasks from './pages/GlobalTasks';
import RFIs from './pages/RFIs';
import Submittals from './pages/Submittals';
import TeamMembers from './pages/TeamMembers';
import ActivityLog from './pages/ActivityLog';
import ClientPortal from './pages/ClientPortal';
import ClientFinancePortal from './pages/ClientFinancePortal';
import PortfolioGantt from './pages/PortfolioGantt';
import FinanceProjects from './pages/FinanceProjects';
import FinanceProjectDetail from './pages/FinanceProjectDetail';
import SharedLists from './pages/SharedLists';
import SharedListDetail from './pages/SharedListDetail';
import PublicSharedList from './pages/PublicSharedList';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import InstallPWAPrompt from './components/InstallPWAPrompt';

// Helper hook: detect mobile viewport
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

// Inner shell: rendered inside Router so it can react to route changes
const AppShell = ({ user, workspace, token, logout }) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // desktop collapse
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false); // mobile drawer

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer open on mobile
  useEffect(() => {
    if (isMobile && mobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, mobileSidebarOpen]);

  return (
    <div className={`app ${isMobile ? 'is-mobile' : ''}`} data-testid="app-shell">
      <Sidebar
        user={user}
        workspace={workspace}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((v) => !v)}
        isMobile={isMobile}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      {isMobile && mobileSidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setMobileSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${isMobile ? 'mobile' : ''}`}>
        <TopBar
          user={user}
          workspace={workspace}
          onLogout={logout}
          sidebarCollapsed={sidebarCollapsed}
          isMobile={isMobile}
          onOpenMobileSidebar={() => setMobileSidebarOpen(true)}
        />
        <div className="page-content">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="/dashboard" element={<Dashboard token={token} user={user} />} />
            <Route path="/boards" element={<Boards token={token} user={user} />} />
            <Route path="/boards/:boardId" element={<BoardDetail token={token} user={user} />} />
            <Route path="/portfolio-gantt" element={<PortfolioGantt token={token} user={user} />} />
            <Route path="/finance-projects" element={<FinanceProjects token={token} user={user} />} />
            <Route path="/finance-projects/:fpId" element={<FinanceProjectDetail token={token} user={user} />} />
            <Route path="/my-work" element={<MyWork token={token} user={user} />} />
            <Route path="/shared-lists" element={<SharedLists token={token} user={user} />} />
            <Route path="/shared-lists/:listId" element={<SharedListDetail token={token} user={user} />} />
            <Route path="/global-tasks" element={<GlobalTasks token={token} user={user} />} />
            <Route path="/rfis" element={<RFIs token={token} user={user} />} />
            <Route path="/submittals" element={<Submittals token={token} user={user} />} />
            <Route path="/team-members" element={<TeamMembers token={token} user={user} />} />
            <Route path="/activity-log" element={<ActivityLog token={token} user={user} />} />
          </Routes>
        </div>
      </div>
      <InstallPWAPrompt />
    </div>
  );
};

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUser();
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setWorkspace(data.workspace);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    }
  };

  const login = (newToken, newUser) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(newUser);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  if (!token) {
    return (
      <Router>
        <Routes>
          <Route path="/client-portal/:token" element={<ClientPortal />} />
          <Route path="/client-finance/:token" element={<ClientFinancePortal />} />
          <Route path="/share/:token" element={<PublicSharedList />} />
          <Route path="/register/:token" element={<Register onLogin={login} />} />
          <Route path="*" element={<Login onLogin={login} />} />
        </Routes>
      </Router>
    );
  }

  if (!user) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Client portal stays public even when token in localStorage exists */}
        <Route path="/client-portal/:token" element={<ClientPortal />} />
        <Route path="/client-finance/:token" element={<ClientFinancePortal />} />
        <Route path="/share/:token" element={<PublicSharedList />} />
        <Route path="*" element={<AppShell user={user} workspace={workspace} token={token} logout={logout} />} />
      </Routes>
    </Router>
  );
}

export default App;
